import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Edit3, LogOut } from "lucide-react";

const CATEGORIES_FALLBACK = ["Luxury Fashion", "Home & Design", "Travel", "Beauty & Wellness", "Lifestyle"];
const PLATFORMS = ["instagram", "tiktok", "pinterest"];
const POST_TYPES = ["post", "reel", "story"];
const ASPECTS = ["square", "portrait", "landscape"];

function SettingsPanel({ onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const u = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { _id, id, ...body } = s;
      // strip empty category rows
      body.categories = (body.categories || []).filter((c) => c.name && c.name.trim());
      await api.put("/settings", body);
      toast.success("Settings saved.");
      onSaved && onSaved();
    } catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-ink/60">Loading...</p>;

  const cats = s.categories || [];
  const updateCat = (i, k, v) => setS((p) => ({ ...p, categories: p.categories.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const addCat = () => setS((p) => ({ ...p, categories: [...(p.categories || []), { name: "", image_url: "" }] }));
  const removeCat = (i) => setS((p) => ({ ...p, categories: p.categories.filter((_, idx) => idx !== i) }));

  const field = (key, label, Comp = Input, extra = {}) => (
    <div>
      <Label className="text-xs uppercase tracking-widest">{label}</Label>
      <Comp value={s[key] || ""} onChange={(e) => u(key, e.target.value)} className="rounded-none mt-2 border-ink" data-testid={`set-${key}`} {...extra} />
    </div>
  );

  return (
    <form onSubmit={save} className="max-w-3xl space-y-8">
      <div>
        <h3 className="font-serif text-2xl mb-4">Social links</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field("instagram_url", "Instagram URL")}
          {field("tiktok_url", "TikTok URL")}
          {field("pinterest_url", "Pinterest URL")}
          {field("youtube_url", "YouTube URL (optional)")}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-4">Payments</h3>
        {field("paypal_url", "PayPal.me link (e.g. https://paypal.me/charliesmith)")}
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-4">Contact</h3>
        {field("contact_email", "Partnership email")}
      </div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Categories</h3>
          <Button type="button" onClick={addCat} className="rounded-none bg-magenta hover:bg-ink text-cream uppercase tracking-widest text-xs" data-testid="cat-add"><Plus className="w-4 h-4 mr-1" />Add category</Button>
        </div>
        <p className="text-xs text-ink/60 mb-3">These appear on the landing grid and in the Product form dropdown. Rename freely; make sure your existing products still match one of the names above.</p>
        <div className="space-y-3">
          {cats.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end border border-ink/10 p-3 bg-cream" data-testid={`cat-row-${i}`}>
              <div className="col-span-4">
                <Label className="text-xs uppercase tracking-widest">Name</Label>
                <Input value={c.name} onChange={(e) => updateCat(i, "name", e.target.value)} className="rounded-none mt-1 border-ink" data-testid={`cat-name-${i}`} />
              </div>
              <div className="col-span-7">
                <Label className="text-xs uppercase tracking-widest">Image URL</Label>
                <Input value={c.image_url} onChange={(e) => updateCat(i, "image_url", e.target.value)} className="rounded-none mt-1 border-ink" data-testid={`cat-img-${i}`} />
              </div>
              <div className="col-span-1">
                <Button type="button" variant="outline" onClick={() => removeCat(i)} className="rounded-none border-ink w-full" data-testid={`cat-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-4">Hero copy</h3>
        <div className="space-y-4">
          {field("hero_tagline", "Tagline (headline)")}
          {field("hero_accent_words", "Highlighted words (comma-separated — these appear in magenta italic)")}
          {field("hero_subtitle", "Subtitle", Textarea)}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-4">Running banner (marquee)</h3>
        <p className="text-xs text-ink/60 mb-3">One phrase per line. Shown scrolling across the site under the hero.</p>
        <Textarea
          value={(s.marquee_items || []).join("\n")}
          onChange={(e) => u("marquee_items", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
          rows={6}
          className="rounded-none mt-1 border-ink font-mono text-sm"
          data-testid="set-marquee_items"
        />
      </div>
      <div>
        <h3 className="font-serif text-2xl mb-4">Partner section</h3>
        <div className="space-y-4">
          {field("partner_headline", "Quote")}
          {field("partner_body", "Body", Textarea)}
        </div>
      </div>
      <Button type="submit" disabled={saving} className="rounded-none bg-ink hover:bg-magenta text-cream uppercase tracking-widest text-xs py-6 px-10" data-testid="settings-save">
        {saving ? "Saving..." : "Save settings"}
      </Button>
    </form>
  );
}

function useCrud(endpoint) {
  const [items, setItems] = useState([]);
  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const remove = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };
  const save = async (body, id) => { if (id) await api.put(`${endpoint}/${id}`, body); else await api.post(endpoint, body); load(); };
  return { items, save, remove, reload: load };
}

function ProductForm({ initial, onSave, onClose, categories }) {
  const cats = (categories && categories.length ? categories.map((c) => c.name) : CATEGORIES_FALLBACK);
  const [f, setF] = useState(initial || { name: "", brand: "", category: cats[0], price_tier: "$$$", image_url: "", affiliate_url: "", description: "", featured: true });
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); onClose(); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Name</Label><Input required value={f.name} onChange={(e) => u("name", e.target.value)} className="rounded-none mt-1" data-testid="p-name" /></div>
        <div><Label>Brand</Label><Input required value={f.brand} onChange={(e) => u("brand", e.target.value)} className="rounded-none mt-1" data-testid="p-brand" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Category</Label>
          <Select value={f.category} onValueChange={(v) => u("category", v)}>
            <SelectTrigger className="rounded-none mt-1" data-testid="p-category"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Price tier</Label><Input value={f.price_tier} onChange={(e) => u("price_tier", e.target.value)} className="rounded-none mt-1" data-testid="p-price" /></div>
      </div>
      <div><Label>Image URL</Label><Input required value={f.image_url} onChange={(e) => u("image_url", e.target.value)} className="rounded-none mt-1" data-testid="p-image" /></div>
      <div><Label>Affiliate URL</Label><Input required value={f.affiliate_url} onChange={(e) => u("affiliate_url", e.target.value)} className="rounded-none mt-1" data-testid="p-url" /></div>
      <div><Label>Description</Label><Textarea value={f.description} onChange={(e) => u("description", e.target.value)} className="rounded-none mt-1" /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => u("featured", e.target.checked)} /> Featured on landing</label>
      <Button type="submit" className="w-full rounded-none bg-ink hover:bg-magenta text-cream uppercase tracking-widest text-xs py-6" data-testid="p-save">Save product</Button>
    </form>
  );
}

function PostForm({ initial, onSave, onClose }) {
  const [f, setF] = useState(initial || { platform: "instagram", post_type: "post", aspect: "square", image_url: "", post_url: "", caption: "" });
  const u = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); onClose(); }} className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Platform</Label>
          <Select value={f.platform} onValueChange={(v) => u("platform", v)}>
            <SelectTrigger className="rounded-none mt-1" data-testid="s-platform"><SelectValue /></SelectTrigger>
            <SelectContent>{PLATFORMS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={f.post_type} onValueChange={(v) => u("post_type", v)}>
            <SelectTrigger className="rounded-none mt-1" data-testid="s-type"><SelectValue /></SelectTrigger>
            <SelectContent>{POST_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Aspect</Label>
          <Select value={f.aspect} onValueChange={(v) => u("aspect", v)}>
            <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{ASPECTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Image URL</Label><Input required value={f.image_url} onChange={(e) => u("image_url", e.target.value)} className="rounded-none mt-1" data-testid="s-image" /></div>
      <div><Label>Post URL</Label><Input required value={f.post_url} onChange={(e) => u("post_url", e.target.value)} className="rounded-none mt-1" data-testid="s-url" /></div>
      <div><Label>Caption</Label><Input value={f.caption} onChange={(e) => u("caption", e.target.value)} className="rounded-none mt-1" /></div>
      <Button type="submit" className="w-full rounded-none bg-ink hover:bg-magenta text-cream uppercase tracking-widest text-xs py-6" data-testid="s-save">Save post</Button>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const products = useCrud("/products");
  const posts = useCrud("/social-posts");
  const [editing, setEditing] = useState(null); // {kind, data}
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (user) api.get("/settings").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, [user]);

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const openNew = (kind) => { setEditing({ kind, data: null }); setOpen(true); };
  const openEdit = (kind, data) => { setEditing({ kind, data }); setOpen(true); };

  const handleSave = async (body) => {
    try {
      if (editing.kind === "product") await products.save(body, editing.data?.id);
      else await posts.save(body, editing.data?.id);
      toast.success("Saved.");
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="min-h-screen bg-cream text-ink font-sans">
      <Toaster position="top-center" />
      <header className="border-b border-ink/10 bg-cream/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-magenta">Admin</p>
            <Link to="/" className="font-serif text-xl">Charlie&rsquo;s Digital Collective</Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ink/60 hidden md:inline">{user.email}</span>
            <Button onClick={logout} variant="outline" className="rounded-none border-ink uppercase tracking-widest text-xs" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <Tabs defaultValue="products">
          <TabsList className="rounded-none bg-cream-2 border border-ink/10 p-1">
            <TabsTrigger value="products" className="rounded-none uppercase tracking-widest text-xs px-6" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="posts" className="rounded-none uppercase tracking-widest text-xs px-6" data-testid="tab-posts">Social posts</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none uppercase tracking-widest text-xs px-6" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl">Products ({products.items.length})</h2>
              <Button onClick={() => openNew("product")} className="rounded-none bg-magenta hover:bg-ink text-cream uppercase tracking-widest text-xs" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" /> New product</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.items.map((p) => (
                <div key={p.id} className="border border-ink/10 bg-cream-2" data-testid={`admin-product-${p.id}`}>
                  <div className="aspect-[4/5] bg-ink overflow-hidden"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-widest text-magenta font-bold">{p.category}</p>
                    <p className="font-serif text-lg leading-tight mt-1">{p.name}</p>
                    <p className="text-xs text-ink/60">{p.brand}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit("product", p)} className="rounded-none flex-1"><Edit3 className="w-3 h-3 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => products.remove(p.id)} className="rounded-none"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-3xl">Social posts ({posts.items.length})</h2>
              <Button onClick={() => openNew("post")} className="rounded-none bg-magenta hover:bg-ink text-cream uppercase tracking-widest text-xs" data-testid="add-post-btn"><Plus className="w-4 h-4 mr-2" /> New post</Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {posts.items.map((p) => (
                <div key={p.id} className="border border-ink/10 bg-cream-2" data-testid={`admin-post-${p.id}`}>
                  <div className="aspect-square bg-ink overflow-hidden"><img src={p.image_url} alt="" className="w-full h-full object-cover" /></div>
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-widest text-magenta font-bold">{p.platform} · {p.post_type}</p>
                    <p className="text-xs mt-1 truncate">{p.caption}</p>
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit("post", p)} className="rounded-none flex-1 h-8"><Edit3 className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => posts.remove(p.id)} className="rounded-none h-8"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-8">
            <div className="mb-6">
              <h2 className="font-serif text-3xl">Site settings</h2>
              <p className="text-sm text-ink/60 mt-2">Categories, social links, PayPal, hero copy, and contact email — all live-editable. Changes appear on the landing page instantly.</p>
            </div>
            <SettingsPanel onSaved={() => api.get("/settings").then((r) => setCategories(r.data.categories || []))} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing?.data ? "Edit" : "New"} {editing?.kind}</DialogTitle></DialogHeader>
          {editing?.kind === "product" && <ProductForm initial={editing.data} onSave={handleSave} onClose={() => setOpen(false)} categories={categories} />}
          {editing?.kind === "post" && <PostForm initial={editing.data} onSave={handleSave} onClose={() => setOpen(false)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
